import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery as useTanStackQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef, type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { useAuth } from '@clerk/react';
import { useAction, usePaginatedQuery } from 'convex/react';
import { api } from '@/lib/api';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge, badgeVariants } from '@/components/ui/badge';
import { toast } from '@/template/lib/toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import ConfirmModal from '@/components/ui/confirm-modal';
import { Mail, X } from 'lucide-react';
import { LoginProviderIcon } from '@/components/features/operator/login-provider-icon';
import { type UserRole, type VerificationStatus, type LoginProvider } from '@/types/validators';
import { type OperatorInvitation, type UserListItem, type UserListSortOption } from '@/types/users';
import { type UsersTabType } from '@/types/operator';
import { formatBerlinDate, formatBerlinDateTime } from '@/lib/berlin-time';
import { didSortingChange, mapSortingToSortKey, type SortMapping } from '@/lib/data-table-state';
import { matchesUserRoleFilter, type UserRoleFilter } from '@/lib/user-list-filters';
import { useBerlinQuarter } from '@/hooks/use-berlin-quarter';
import { cn } from '@/lib/utils';

/** Extended verification status that includes 'unverified' for UI display */
type ExtendedVerificationStatus = VerificationStatus | 'unverified';

const getStatusLabel = (t: (key: string) => string, status: ExtendedVerificationStatus): string => t(`usersIndex.verificationStatus.${status}`);

/**
 * Maps verification status to Badge variant
 */
const getStatusVariant = (status: ExtendedVerificationStatus): 'default' | 'secondary' | 'destructive' | 'warning' => {
  const variants: Record<ExtendedVerificationStatus, 'default' | 'secondary' | 'destructive' | 'warning'> = {
    pending: 'warning',
    verified: 'default',
    rejected: 'destructive',
    changes_requested: 'warning',
    expired_insurance: 'destructive',
    unapproved: 'secondary',
    unverified: 'secondary',
  };
  return variants[status] || 'secondary';
};

const getRoleLabel = (t: (key: string) => string, role: UserRole): string => t(`usersIndex.roles.${role}`);

const userSortColumns = ['customerNumber', 'firstName', 'lastName', 'email', 'loginProvider', 'role', 'registered', 'lastLogin'] as const;
type UserSortColumn = (typeof userSortColumns)[number];

const userSortMapping: SortMapping<UserSortColumn, UserListSortOption> = {
  customerNumber: { asc: 'customer_number_asc', desc: 'customer_number_desc' },
  firstName: { asc: 'first_name_asc', desc: 'first_name_desc' },
  lastName: { asc: 'last_name_asc', desc: 'last_name_desc' },
  email: { asc: 'email_asc', desc: 'email_desc' },
  loginProvider: { asc: 'login_provider_asc', desc: 'login_provider_desc' },
  role: { asc: 'role_asc', desc: 'role_desc' },
  registered: { asc: 'created_asc', desc: 'created_desc' },
  lastLogin: { asc: 'last_login_asc', desc: 'last_login_desc' },
};

const userRoles: readonly UserRole[] = ['substitute', 'provider', 'operator', 'admin'];
const USERS_PAGE_SIZE = 50;

const isUserRole = (role: string | undefined): role is UserRole => Boolean(role && userRoles.includes(role as UserRole));

const getUserRole = (user: UserListItem): UserRole | null => {
  if (isUserRole(user.role)) return user.role;

  const metadataRoles = user.roles ?? user.publicMetadata?.roles ?? [];
  const matchedRole = metadataRoles.find(isUserRole);
  return matchedRole ?? null;
};

// Bug #11 Fix: Custom debounce hook for search
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const OperatorsUsersIndexPage = () => {
  const { t, i18n } = useTranslation('operator');
  const [activeTab, setActiveTab] = useState<UsersTabType>('users');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRoleFilter>('all');
  const [targetUserCount, setTargetUserCount] = useState(USERS_PAGE_SIZE);
  const [withoutDuties, setWithoutDuties] = useState<'all' | 'never' | 'registered_this_quarter'>('all');
  const quarterStart = useBerlinQuarter(withoutDuties === 'registered_this_quarter');
  const [userSorting, setUserSorting] = useState<SortingState>([{ id: 'registered', desc: false }]);
  const [invitationSorting, setInvitationSorting] = useState<SortingState>([]);
  // Bug #11 Fix: Debounce search input by 300ms
  const debouncedSearch = useDebounce(q, 300);
  const userSortBy = mapSortingToSortKey(userSorting, userSortColumns, userSortMapping, 'created_asc');
  const isBackendFilterActive = debouncedSearch.trim().length > 0 || withoutDuties !== 'all';

  const createUser = useAction(api.app.users.actions.operatorCreateUser as any);
  const inviteUser = useAction(api.app.operatorInvitations.inviteUser as any);
  const queryClient = useQueryClient();
  // Namespace the cached invitations by the signed-in operator. The QueryClient is module-level
  // and survives an in-session account switch, so an unscoped key could surface a previous
  // operator's invitee emails until the stale window elapses.
  const { userId: clerkUserId } = useAuth();
  const invitationsQueryKey = ['operatorInvitations', clerkUserId] as const;

  // Invitations state
  const [invitationStatusFilter, setInvitationStatusFilter] = useState<string>('all');
  const [invitationSearch, setInvitationSearch] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeConfirmModalOpen, setRevokeConfirmModalOpen] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<string | null>(null);

  // Backend actions for invitations
  const listInvitationsAction = useAction(api.app.operatorInvitations.listInvitations as any);
  const resendInvitationAction = useAction(api.app.operatorInvitations.resendInvitation as any);
  const revokeInvitationAction = useAction(api.app.operatorInvitations.revokeInvitation as any);

  // Create user modal state
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verify, setVerify] = useState(true);
  const [generateRandom, setGenerateRandom] = useState(false);
  const [creating, setCreating] = useState(false);
  const [role, setRole] = useState<UserRole | ''>('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [generatedPw, setGeneratedPw] = useState<string | null>(null);

  // Invite user modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Exclude<UserRole, 'admin'> | ''>('');
  const [inviting, setInviting] = useState(false);

  // Pagination state - Bug #2 & #10 Fix: Track pagination state properly
  const usersData = usePaginatedQuery(api.app.users.queries.listUsersWithProfiles, {
    sortBy: userSortBy,
    ...(debouncedSearch && { q: debouncedSearch }),
    ...(withoutDuties !== 'all' && { withoutDuties }),
    ...(withoutDuties === 'registered_this_quarter' && { quarterStart }),
  }, { initialNumItems: USERS_PAGE_SIZE }) as
    | {
        results: UserListItem[];
        status: 'LoadingFirstPage' | 'CanLoadMore' | 'LoadingMore' | 'Exhausted';
        loadMore: (numItems: number) => void;
      }
    | undefined;

  const filteredUsers = useMemo(() => {
    return (usersData?.results ?? []).filter((user) => matchesUserRoleFilter(user, roleFilter));
  }, [usersData?.results, roleFilter]);

  const navigate = useNavigate();

  const invitationsQuery = useTanStackQuery({
    queryKey: invitationsQueryKey,
    queryFn: () => listInvitationsAction({}) as Promise<OperatorInvitation[]>,
    enabled: activeTab === 'invitations',
    staleTime: 60_000,
    // Keep retry bounded: the underlying Clerk action can be slow to fail, and the
    // error surfaces in the UI below, so the default 3x backoff just lengthens that window.
    retry: 1,
  });
  const invitations = invitationsQuery.data ?? [];
  // Skeletons only on the initial load; keep cached rows visible during background refetches
  // (e.g. after resend/revoke/invite invalidation). isLoading is false whenever cached data exists.
  const invitationsLoading = invitationsQuery.isLoading;
  const usersLoading =
    usersData?.status === 'LoadingFirstPage' ||
    (isBackendFilterActive && usersData?.status !== 'Exhausted' && filteredUsers.length === 0);

  useEffect(() => {
    setTargetUserCount(USERS_PAGE_SIZE);
  }, [debouncedSearch, userSortBy, withoutDuties, quarterStart]);

  useEffect(() => {
    if (activeTab !== 'users' || !isBackendFilterActive || usersData?.status !== 'CanLoadMore' || usersData.results.length >= targetUserCount) return;

    usersData.loadMore(USERS_PAGE_SIZE);
  }, [activeTab, isBackendFilterActive, targetUserCount, usersData]);

  // Date formatter for current locale
  const formatDate = useCallback((timestamp: number): string => {
    return formatBerlinDateTime(timestamp, i18n.language || 'de-DE');
  }, [i18n.language]);

  const formatDateOnly = useCallback((timestamp: number): string => {
    return formatBerlinDate(timestamp, i18n.language.startsWith('en') ? 'en-US' : 'de-DE');
  }, [i18n.language]);

  // Status badge variant mapper
  const getInvitationStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'warning' => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
      pending: 'warning',
      accepted: 'default',
      revoked: 'secondary',
      expired: 'destructive',
    };
    return variants[status] || 'secondary';
  };

  // Status label mapper
  const getInvitationStatusLabel = (status: string): string => t(`usersIndex.invitations.status.${status}`);

  // Filter invitations based on search and status
  const filteredInvitations = invitations.filter((inv) => {
    const matchesSearch = inv.emailAddress.toLowerCase().includes(invitationSearch.toLowerCase());
    const matchesStatus = invitationStatusFilter === 'all' || inv.status === invitationStatusFilter;
    return matchesSearch && matchesStatus;
  });

  // Resend invitation handler
  const handleResendInvitation = async (invitationId: string, email: string) => {
    setResendingId(invitationId);
    try {
      await resendInvitationAction({ invitationId });
      toast.success(t('operator:invitations.toasts.resendSuccess', { email }));
      // refetchType 'all' so the refetch also reaches the invitations query while it is disabled
      // (the invite modal is reachable from the users tab); otherwise the new invite would not
      // appear until the 60s stale window elapsed after switching back to the invitations tab.
      await queryClient.invalidateQueries({ queryKey: invitationsQueryKey, refetchType: 'all' });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('usersIndex.toasts.unknownError');
      toast.error(t('operator:invitations.toasts.resendError', { error: errorMessage }));
    } finally {
      setResendingId(null);
    }
  };

  // Revoke invitation handler
  const handleRevokeInvitation = async () => {
    if (!invitationToRevoke) return;

    const invitation = invitations.find((inv) => inv.id === invitationToRevoke);
    if (!invitation) return;

    setRevokingId(invitationToRevoke);
    try {
      await revokeInvitationAction({ invitationId: invitationToRevoke });
      toast.success(t('operator:invitations.toasts.revokeSuccess', { email: invitation.emailAddress }));
      // refetchType 'all' so the refetch also reaches the invitations query while it is disabled
      // (the invite modal is reachable from the users tab); otherwise the new invite would not
      // appear until the 60s stale window elapsed after switching back to the invitations tab.
      await queryClient.invalidateQueries({ queryKey: invitationsQueryKey, refetchType: 'all' });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('usersIndex.toasts.unknownError');
      toast.error(t('operator:invitations.toasts.revokeError', { error: errorMessage }));
    } finally {
      setRevokingId(null);
      setRevokeConfirmModalOpen(false);
      setInvitationToRevoke(null);
    }
  };

  const handleLoadMore = () => {
    if (usersData?.status === 'CanLoadMore') {
      if (isBackendFilterActive) setTargetUserCount(usersData.results.length + USERS_PAGE_SIZE);
      else usersData.loadMore(USERS_PAGE_SIZE);
    }
  };

  const handleUserSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setUserSorting((currentSorting) => {
      const nextSorting = typeof updater === 'function' ? updater(currentSorting) : updater;
      return didSortingChange(currentSorting, nextSorting) ? nextSorting : currentSorting;
    });
  }, []);

  const userColumns = useMemo<ColumnDef<UserListItem, unknown>[]>(() => {
    const dash = t('usersIndex.common.dash');

    return [
      {
        accessorKey: 'customerNumber',
        id: 'customerNumber',
        header: t('usersIndex.table.columns.customerNumber'),
        cell: ({ row }) => <span className='font-mono text-xs'>{row.original.customerNumber || dash}</span>,
      },
      {
        accessorKey: 'firstName',
        id: 'firstName',
        header: t('usersIndex.table.columns.firstName'),
        cell: ({ row }) => row.original.firstName || dash,
      },
      {
        accessorKey: 'lastName',
        id: 'lastName',
        header: t('usersIndex.table.columns.lastName'),
        cell: ({ row }) => row.original.lastName || dash,
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: t('usersIndex.table.columns.email'),
      },
      {
        accessorKey: 'loginProvider',
        id: 'loginProvider',
        header: t('usersIndex.table.columns.provider'),
        cell: ({ row }) => (row.original.loginProvider ? <LoginProviderIcon provider={row.original.loginProvider as LoginProvider} /> : dash),
      },
      {
        id: 'role',
        accessorFn: (user) => getUserRole(user) ?? '',
        header: t('usersIndex.table.columns.role'),
        cell: ({ row }) => {
          const roleValue = getUserRole(row.original);
          return roleValue ? getRoleLabel(t, roleValue) : dash;
        },
      },
      {
        id: 'registered',
        accessorFn: (user) => user._creationTime ?? null,
        header: t('usersIndex.table.columns.registered'),
        cell: ({ row }) => (row.original._creationTime ? formatDateOnly(row.original._creationTime) : dash),
      },
      {
        id: 'lastLogin',
        accessorFn: (user) => user.lastSignInAt ?? null,
        header: t('usersIndex.table.columns.lastLogin'),
        cell: ({ row }) => (row.original.lastSignInAt ? formatDate(row.original.lastSignInAt) : dash),
      },
      {
        id: 'location',
        accessorFn: (user) => user.profile?.location?.city ?? '',
        header: t('usersIndex.table.columns.location'),
        enableSorting: false,
        cell: ({ row }) => row.original.profile?.location?.city || dash,
      },
      {
        id: 'status',
        accessorFn: (user) => user.profile?.verificationStatus ?? 'unverified',
        header: t('usersIndex.table.columns.status'),
        enableSorting: false,
        cell: ({ row }) => {
          const roleValue = getUserRole(row.original);
          const verificationStatus: ExtendedVerificationStatus = (row.original.profile?.verificationStatus as ExtendedVerificationStatus) || 'unverified';

          return roleValue === 'provider' || roleValue === 'operator' ? (
            <span className='text-muted-foreground'>{t('usersIndex.common.hyphen')}</span>
          ) : (
            <Badge variant={getStatusVariant(verificationStatus)}>{getStatusLabel(t, verificationStatus)}</Badge>
          );
        },
      },
      {
        id: 'actions',
        header: t('usersIndex.table.columns.actions'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <Button
              variant='ghost'
              size='icon'
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                navigate({ to: `/operators/users/${row.original._id}` });
              }}
              aria-label={t('usersIndex.table.actions.viewDetailsAria', { firstName: row.original.firstName || t('usersIndex.common.userFallback'), lastName: row.original.lastName || '' })}
            >
              <Icons.ChevronRight className='h-4 w-4' />
            </Button>
          </div>
        ),
      },
    ];
  }, [formatDate, formatDateOnly, navigate, t]);

  const invitationColumns = useMemo<ColumnDef<OperatorInvitation, unknown>[]>(() => {
    return [
      {
        accessorKey: 'emailAddress',
        id: 'emailAddress',
        header: t('operator:invitations.table.email'),
      },
      {
        accessorKey: 'role',
        id: 'role',
        header: t('operator:invitations.table.role'),
        cell: ({ row }) => (
          <Badge variant='secondary'>{isUserRole(row.original.role) ? getRoleLabel(t, row.original.role) : row.original.role}</Badge>
        ),
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: t('operator:invitations.table.status'),
        cell: ({ row }) => <Badge variant={getInvitationStatusVariant(row.original.status)}>{getInvitationStatusLabel(row.original.status)}</Badge>,
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: t('operator:invitations.table.sentAt'),
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'actions',
        header: t('operator:invitations.table.actions'),
        enableSorting: false,
        cell: ({ row }) => {
          const invitation = row.original;

          return (
            <div className='flex justify-end gap-2'>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleResendInvitation(invitation.id, invitation.emailAddress)}
                disabled={resendingId === invitation.id || invitation.status === 'accepted'}
                aria-label={t('usersIndex.invitations.actions.resendAriaLabel', { email: invitation.emailAddress })}
              >
                {resendingId === invitation.id ? <Icons.Loader className='h-4 w-4 animate-spin' /> : <Mail className='h-4 w-4' />}
              </Button>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => {
                  setInvitationToRevoke(invitation.id);
                  setRevokeConfirmModalOpen(true);
                }}
                disabled={revokingId === invitation.id || invitation.status === 'accepted' || invitation.status === 'revoked'}
                aria-label={t('usersIndex.invitations.actions.revokeAriaLabel', { email: invitation.emailAddress })}
              >
                {revokingId === invitation.id ? <Icons.Loader className='h-4 w-4 animate-spin' /> : <X className='h-4 w-4' />}
              </Button>
            </div>
          );
        },
      },
    ];
  }, [formatDate, getInvitationStatusLabel, getInvitationStatusVariant, handleResendInvitation, resendingId, revokingId, t]);

  // Bug #13 Fix: Helper to reset form state
  const resetCreateUserForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setGenerateRandom(false);
    setVerify(true);
    setRole('');
    setFirst('');
    setLast('');
    setGeneratedPw(null);
  }, []);

  // Bug #13 Fix: Handle modal close - only reset form when modal closes
  const handleCreateUserModalChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        // Reset form state when modal closes
        resetCreateUserForm();
      }
    },
    [resetCreateUserForm]
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setGeneratedPw(null);
    try {
      const res = await createUser({
        email,
        password: generateRandom ? undefined : password,
        verify,
        generateRandom,
        role: role || undefined,
        firstName: first || undefined,
        lastName: last || undefined,
      });
      if (res?.onboardingEmail?.success === false) {
        toast.error(t('usersIndex.createUser.toasts.createdEmailFailed', { error: res.onboardingEmail.error ?? t('usersIndex.createUser.toasts.unknownEmailError') }));
      } else {
        toast.success(t('usersIndex.createUser.toasts.createdAndEmailed'));
      }
      if (res?.generatedPassword) {
        // Bug #13 Fix: Keep modal open and show generated password
        // Form will be cleared when modal closes
        setGeneratedPw(res.generatedPassword);
      } else {
        // No generated password - close modal and reset form
        setOpen(false);
        // Form reset happens automatically via handleCreateUserModalChange
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const onInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await inviteUser({
        email: inviteEmail,
        role: inviteRole || undefined,
      });
      toast.success(t('operator:invitation.toasts.success', { email: inviteEmail }));
      // refetchType 'all' so the refetch also reaches the invitations query while it is disabled
      // (the invite modal is reachable from the users tab); otherwise the new invite would not
      // appear until the 60s stale window elapsed after switching back to the invitations tab.
      await queryClient.invalidateQueries({ queryKey: invitationsQueryKey, refetchType: 'all' });
      setInviteModalOpen(false);
      setInviteEmail('');
      setInviteRole('');
    } catch (err: unknown) {
      // Extract error message from various possible error formats
      let errorMessage = t('usersIndex.toasts.unknownError');

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        // Handle Convex error objects which may have different structures
        const errObj = err as Record<string, unknown>;
        errorMessage = (errObj['message'] as string) || (errObj['data'] as string) || String(errObj);
      }

      // Clean up Convex error message format
      // Extract the meaningful error after "Uncaught Error: " or "Clerk API error: "
      if (errorMessage.includes('Uncaught Error:')) {
        // Extract everything after "Uncaught Error: " and before "Called by client"
        const match = errorMessage.match(/Uncaught Error:\s*(.+?)(?:\s*Called by|$)/s);
        if (match?.[1]) {
          errorMessage = match[1].trim();
        }
      }

      // If it's a Clerk API error, extract just the Clerk message
      if (errorMessage.includes('Clerk API error:')) {
        const clerkMatch = errorMessage.match(/Clerk API error:\s*(.+?)$/s);
        if (clerkMatch?.[1]) {
          errorMessage = clerkMatch[1].trim();
        }
      }

      // Remove any remaining "Failed to invite user: " prefix for cleaner message
      errorMessage = errorMessage.replace(/^Failed to invite user:\s*/i, '');

      // Log full error for debugging
      console.error('Invitation error:', err);
      console.error('Cleaned message:', errorMessage);

      // Use translation with fallback to direct string concatenation
      const translatedError = t('operator:invitation.toasts.error', { error: errorMessage });

      // Fallback if interpolation fails (shows as "(error)")
      if (translatedError.includes('(error)')) {
        toast.error(t('usersIndex.toasts.inviteErrorFallback', { error: errorMessage }));
      } else {
        toast.error(translatedError);
      }
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className='p-4 flex flex-col h-screen'>
      <div className='flex items-center justify-between mb-3'>
        <h1 className='text-2xl font-semibold'>{t('usersIndex.title')}</h1>
        <div className='flex gap-2'>
          {/* Bug #13 Fix: Use handleCreateUserModalChange to reset form on close */}
          <Dialog open={open} onOpenChange={handleCreateUserModalChange}>
            <DialogTrigger render={<Button />}>{t('usersIndex.createUser.openButton')}</DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('usersIndex.createUser.title')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className='space-y-3'>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1'>
                    <Label htmlFor='first'>{t('usersIndex.createUser.fields.firstName')}</Label>
                    <Input id='first' value={first} onChange={(e) => setFirst(e.target.value)} />
                  </div>
                  <div className='space-y-1'>
                    <Label htmlFor='last'>{t('usersIndex.createUser.fields.lastName')}</Label>
                    <Input id='last' value={last} onChange={(e) => setLast(e.target.value)} />
                  </div>
                </div>
                <div className='space-y-1'>
                  <Label htmlFor='email'>{t('usersIndex.createUser.fields.email')}</Label>
                  <Input id='email' type='email' value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className='flex items-center gap-2'>
                  <input id='verify' type='checkbox' checked={verify} onChange={(e) => setVerify(e.target.checked)} />
                  <Label htmlFor='verify'>{t('usersIndex.createUser.fields.verifyEmailNow')}</Label>
                </div>
                <div className='space-y-1'>
                  <Label>{t('usersIndex.createUser.fields.role')}</Label>
                  <Select value={role} onValueChange={(v: string | null) => setRole(v as UserRole | '')}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('usersIndex.createUser.fields.noRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='substitute'>{t('usersIndex.roles.substitute')}</SelectItem>
                      <SelectItem value='provider'>{t('usersIndex.roles.provider')}</SelectItem>
                      <SelectItem value='operator'>{t('usersIndex.roles.operator')}</SelectItem>
                      <SelectItem value='admin'>{t('usersIndex.roles.admin')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <input id='gen' type='checkbox' checked={generateRandom} onChange={(e) => setGenerateRandom(e.target.checked)} />
                    <Label htmlFor='gen'>{t('usersIndex.createUser.fields.generateRandomPassword')}</Label>
                  </div>
                  {!generateRandom && (
                    <div className='space-y-1'>
                      <Label htmlFor='pw'>{t('usersIndex.createUser.fields.password')}</Label>
                      <Input id='pw' type='password' value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </div>
                  )}
                </div>

                {generatedPw && (
                  <div className='rounded border p-2 text-sm'>
                    <div className='font-medium'>{t('usersIndex.createUser.generatedPassword')}</div>
                    <div className='select-all break-all mt-1'>{generatedPw}</div>
                  </div>
                )}
                <DialogFooter>
                  <Button type='button' variant='secondary' onClick={() => handleCreateUserModalChange(false)} disabled={creating}>
                    {t('usersIndex.createUser.actions.cancel')}
                  </Button>
                  <Button type='submit' disabled={creating || !email || (!generateRandom && !password)}>
                    {creating ? t('usersIndex.createUser.actions.creating') : t('usersIndex.createUser.actions.create')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
            <DialogTrigger render={<Button variant='outline' />}>
              <Icons.Mail className='size-4 mr-2' />
              {t('operator:invitation.buttonLabel')}
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('operator:invitation.modalTitle')}</DialogTitle>
                <DialogDescription>{t('operator:invitation.modalDescription')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={onInviteSubmit} className='space-y-3'>
                <div className='space-y-1'>
                  <Label htmlFor='invite-email'>{t('operator:invitation.fields.email')}</Label>
                  <Input id='invite-email' type='email' value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t('operator:invitation.fields.emailPlaceholder')} required />
                </div>
                <div className='space-y-1'>
                  <Label htmlFor='invite-role'>{t('operator:invitation.fields.role')}</Label>
                  <Select value={inviteRole} onValueChange={(v: string | null) => setInviteRole(v as Exclude<UserRole, 'admin'> | '')}>
                    <SelectTrigger id='invite-role'>
                      <SelectValue placeholder={t('operator:invitation.fields.selectRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='provider'>{t('operator:invitation.roles.provider')}</SelectItem>
                      <SelectItem value='substitute'>{t('operator:invitation.roles.substitute')}</SelectItem>
                      <SelectItem value='operator'>{t('operator:invitation.roles.operator')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type='button' variant='secondary' onClick={() => setInviteModalOpen(false)} disabled={inviting}>
                    {t('operator:invitation.actions.cancel')}
                  </Button>
                  <Button type='submit' disabled={inviting || !inviteEmail || !inviteRole}>
                    {inviting ? t('operator:invitation.actions.sending') : t('operator:invitation.actions.send')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v: string | null) => setActiveTab(v as 'users' | 'invitations')} className='flex-1 flex flex-col min-h-0'>
        <TabsList>
          <TabsTrigger value='users'>{t('usersIndex.tabs.users')}</TabsTrigger>
          <TabsTrigger value='invitations'>{t('operator:invitations.tabLabel')}</TabsTrigger>
        </TabsList>

        <TabsContent value='users' className='flex-1 flex flex-col min-h-0'>
          <div className='mb-3 flex flex-wrap items-center gap-2'>
            <div className='w-full max-w-md'>
              <Input placeholder={t('usersIndex.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className='flex items-center gap-2'>
              <button type='button' className={cn(badgeVariants({ variant: roleFilter === 'all' ? 'default' : 'secondary' }), 'cursor-pointer border-transparent')} onClick={() => { setRoleFilter('all'); setWithoutDuties('all'); }} aria-pressed={roleFilter === 'all'}>
                {t('usersIndex.roleFilters.all')}
              </button>
              <button type='button' className={cn(badgeVariants({ variant: roleFilter === 'provider' ? 'default' : 'secondary' }), 'cursor-pointer border-transparent')} onClick={() => setRoleFilter('provider')} aria-pressed={roleFilter === 'provider'}>
                {t('usersIndex.roleFilters.provider')}
              </button>
              <button type='button' className={cn(badgeVariants({ variant: roleFilter === 'substitute' ? 'default' : 'secondary' }), 'cursor-pointer border-transparent')} onClick={() => { setRoleFilter('substitute'); setWithoutDuties('all'); }} aria-pressed={roleFilter === 'substitute'}>
                {t('usersIndex.roleFilters.substitute')}
              </button>
            </div>
            <div className='flex w-full flex-col gap-1 sm:w-auto'>
              <Label htmlFor='users-without-duties'>{t('usersIndex.dutyFilter.label')}</Label>
              <Select value={withoutDuties} onValueChange={(value) => {
                if (value !== 'all' && value !== 'never' && value !== 'registered_this_quarter') return;
                setWithoutDuties(value);
                if (value !== 'all') setRoleFilter('provider');
              }}>
                <SelectTrigger id='users-without-duties' className='min-h-11 w-full sm:w-80'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all' className='min-h-11'>{t('usersIndex.dutyFilter.all')}</SelectItem>
                  <SelectItem value='never' className='min-h-11'>{t('usersIndex.dutyFilter.never')}</SelectItem>
                  <SelectItem value='registered_this_quarter' className='min-h-11'>{t('usersIndex.dutyFilter.quarter')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className='flex-1 min-h-0 overflow-auto' aria-busy={usersLoading || usersData?.status === 'LoadingMore'}>
            <span role='status' className='sr-only'>
              {usersLoading || usersData?.status === 'LoadingMore'
                ? t('usersIndex.dutyFilter.loading')
                : withoutDuties !== 'all' ? t('usersIndex.dutyFilter.loaded', { count: filteredUsers.length }) : ''}
            </span>
            <DataTable
              columns={userColumns}
              data={filteredUsers}
              sorting={userSorting}
              onSortingChange={handleUserSortingChange}
              manualSorting
              isLoading={usersLoading && filteredUsers.length === 0}
              loadingRowCount={8}
              emptyState={t(withoutDuties === 'all' ? 'usersIndex.states.noUsersFound' : 'usersIndex.dutyFilter.empty')}
              getRowId={(user) => user._id}
              onRowClick={(row) => navigate({ to: `/operators/users/${row.original._id}` })}
              headerClassName='sticky top-0 z-10 bg-background'
            />
          </div>
          {/* Load More Button - gate on raw page status, not the client-side role filter,
              so a filter that excludes every loaded row can still page into later results. */}
          {usersData?.status === 'CanLoadMore' && (
            <div className='flex justify-center py-4'>
              <Button variant='outline' onClick={handleLoadMore}>
                <Icons.ChevronDown className='size-4 mr-2' />
                {t('usersIndex.actions.loadMore')}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value='invitations' className='flex-1 flex flex-col min-h-0'>
          {/* Search and Filter Controls */}
          <div className='flex gap-3 mb-3'>
            <div className='flex-1 max-w-md'>
              <Input placeholder={t('operator:invitations.filters.searchPlaceholder')} value={invitationSearch} onChange={(e) => setInvitationSearch(e.target.value)} />
            </div>
            <Select
              value={invitationStatusFilter}
              onValueChange={(v) => {
                if (v !== null) setInvitationStatusFilter(v);
              }}
            >
              <SelectTrigger className='w-[180px]'>
                <SelectValue placeholder={t('usersIndex.invitations.statusFilterPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('operator:invitations.filters.all')}</SelectItem>
                <SelectItem value='pending'>{t('operator:invitations.filters.pending')}</SelectItem>
                <SelectItem value='accepted'>{t('operator:invitations.filters.accepted')}</SelectItem>
                <SelectItem value='revoked'>{t('operator:invitations.filters.revoked')}</SelectItem>
                <SelectItem value='expired'>{t('operator:invitations.filters.expired')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Invitations Table */}
          <div className='flex-1 min-h-0 overflow-auto'>
            {invitationsLoading ? (
              <div className='p-6 space-y-4'>
                <Skeleton className='h-12 w-full' />
                <Skeleton className='h-12 w-full' />
                <Skeleton className='h-12 w-full' />
                <Skeleton className='h-12 w-full' />
              </div>
            ) : invitationsQuery.isError ? (
              <div className='flex flex-col items-center gap-3 p-10 text-center'>
                <p className='font-medium'>{t('operator:invitations.error.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('operator:invitations.error.description')}</p>
                <Button variant='outline' size='sm' onClick={() => invitationsQuery.refetch()} disabled={invitationsQuery.isFetching}>
                  {invitationsQuery.isFetching ? <Icons.Loader className='h-4 w-4 mr-2 animate-spin' /> : <Icons.RefreshCw className='h-4 w-4 mr-2' />}
                  {t('operator:invitations.error.retry')}
                </Button>
              </div>
            ) : (
              <DataTable
                columns={invitationColumns}
                data={filteredInvitations}
                sorting={invitationSorting}
                onSortingChange={setInvitationSorting}
                emptyState={
                  <div className='flex flex-col items-center gap-2'>
                    <p className='font-medium'>{t('operator:invitations.empty.title')}</p>
                    <p className='text-xs'>{t('operator:invitations.empty.description')}</p>
                  </div>
                }
                getRowId={(invitation) => invitation.id}
                headerClassName='sticky top-0 z-10 bg-background'
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Revoke Confirmation Modal */}
      <ConfirmModal
        isOpen={revokeConfirmModalOpen}
        title={t('operator:invitations.confirmRevoke.title')}
        description={
          invitationToRevoke
            ? t('operator:invitations.confirmRevoke.description', {
                email: invitations.find((inv) => inv.id === invitationToRevoke)?.emailAddress || '',
              })
            : ''
        }
        confirmText={t('operator:invitations.confirmRevoke.confirm')}
        cancelText={t('operator:invitations.confirmRevoke.cancel')}
        onConfirm={handleRevokeInvitation}
        onCancel={() => {
          setRevokeConfirmModalOpen(false);
          setInvitationToRevoke(null);
        }}
        loading={revokingId !== null}
      />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/users/')({
  component: OperatorsUsersIndexPage,
});
